import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSurveyStore } from 'Store/Survey'
import { shallow } from 'zustand/shallow'
import moment from 'moment'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

import {
  apiToInstallationForm,
  apiToShippingForm,
  createInitialInstallation,
  createInitialShipping,
  installationFormToApi,
  shippingFormToApi,
  type WorkOpeningNoticeInstallationForm,
  type WorkOpeningNoticeShippingForm,
} from './apiMapper'
import useFetchSurveyDetail from '../../useFetchSurveyDetail'
import {
  createEmptyInstallationLineItem,
  createEmptyLineItem,
  WORK_OPENING_NOTICE_TYPE_OPTIONS,
  type DeliverySiteState,
  type HeaderDocumentState,
  type InstallationContractState,
  type InstallationCraneScheduleState,
  type InstallationCustomerDutyKey,
  type InstallationCustomerDutyState,
  type InstallationFinanceState,
  type InstallationFinanceStatus,
  type InstallationLineItem,
  type InstallationPileCode,
  type InstallationPilingSiteState,
  type InstallationRigFooterState,
  type InstallationSignatureBlock,
  type InstallationSignaturesState,
  type LineItem,
  type PaymentState,
  type PileCode,
  type WorkOpeningNoticeType,
} from './types'

const useViewModel = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const isView = location.search.includes('view')

  const {
    getWorkOpeningNoticeShipping,
    putWorkOpeningNoticeShipping,
    getWorkOpeningNoticeInstallation,
    putWorkOpeningNoticeInstallation,
    downloadWorkOpeningNoticePdf,
  } = useSurveyStore(
    state => ({
      getWorkOpeningNoticeShipping: state.getWorkOpeningNoticeShipping,
      putWorkOpeningNoticeShipping: state.putWorkOpeningNoticeShipping,
      getWorkOpeningNoticeInstallation: state.getWorkOpeningNoticeInstallation,
      putWorkOpeningNoticeInstallation: state.putWorkOpeningNoticeInstallation,
      downloadWorkOpeningNoticePdf: state.downloadWorkOpeningNoticePdf,
    }),
    shallow
  )
  const { survey, isSurveyLoaded } = useFetchSurveyDetail()
  const saleOrder = survey?.saleOrder

  const surveyPrefill = useMemo(
    () => ({
      customerTitle: survey.customerTitle ?? '',
      division: survey.division ?? '',
      productionOrderNo: saleOrder?.code ?? survey.saleOrder?.code ?? '',
      documentDate:
        survey.updatedAt && moment(survey.updatedAt).isValid()
          ? moment(survey.updatedAt).toDate()
          : moment().toDate(),
    }),
    [survey.customerTitle, survey.division, survey.updatedAt, survey.saleOrder?.code, saleOrder?.code]
  )

  const [shipping, setShipping] = useState<WorkOpeningNoticeShippingForm>(() =>
    createInitialShipping(surveyPrefill)
  )
  const [installation, setInstallation] = useState<WorkOpeningNoticeInstallationForm>(() =>
    createInitialInstallation(surveyPrefill)
  )
  const [noticeType, setNoticeType] = useState<WorkOpeningNoticeType>('shipping')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const fetchData = useCallback(async () => {
    if (!id || !isSurveyLoaded) return
    setLoading(true)
    const surveyId = parseInt(id)
    const [shippingRes, installationRes] = await Promise.all([
      getWorkOpeningNoticeShipping(surveyId),
      getWorkOpeningNoticeInstallation(surveyId),
    ])

    if (!shippingRes.success) {
      toast.error(shippingRes.data)
      setLoading(false)
      return
    }
    if (!installationRes.success) {
      toast.error(installationRes.data)
      setLoading(false)
      return
    }

    setShipping(apiToShippingForm(shippingRes.data, surveyPrefill))
    setInstallation(apiToInstallationForm(installationRes.data, surveyPrefill))
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWorkOpeningNoticeInstallation, getWorkOpeningNoticeShipping, id, isSurveyLoaded])

  useEffect(() => {
    if (id && isSurveyLoaded) {
      fetchData()
    }
  }, [fetchData, id, isSurveyLoaded])

  const noticeTitle = useMemo(
    () =>
      WORK_OPENING_NOTICE_TYPE_OPTIONS.find(o => o.value === noticeType)?.title ??
      WORK_OPENING_NOTICE_TYPE_OPTIONS[0].title,
    [noticeType]
  )

  const headerDoc =
    noticeType === 'shipping' ? shipping.headerDoc : installation.headerDoc

  const updateHeaderDoc = (field: keyof HeaderDocumentState, value: any) => {
    if (noticeType === 'shipping') {
      setShipping(prev => ({
        ...prev,
        headerDoc: { ...prev.headerDoc, [field]: value },
      }))
    } else {
      setInstallation(prev => ({
        ...prev,
        headerDoc: { ...prev.headerDoc, [field]: value },
      }))
    }
  }

  const sendTo = noticeType === 'shipping' ? shipping.sendTo : installation.sendTo
  const setSendTo = (value: string) => {
    if (noticeType === 'shipping') {
      setShipping(prev => ({ ...prev, sendTo: value }))
    } else {
      setInstallation(prev => ({ ...prev, sendTo: value }))
    }
  }

  const items = shipping.items
  const addItemRow = () => {
    setShipping(prev => ({ ...prev, items: [...prev.items, createEmptyLineItem()] }))
  }
  const removeItemRow = (rowId: string) => {
    setShipping(prev => ({
      ...prev,
      items: prev.items.length <= 1 ? prev.items : prev.items.filter(row => row.id !== rowId),
    }))
  }
  const updateItemRow = (rowId: string, field: keyof Omit<LineItem, 'id'>, value: string) => {
    setShipping(prev => ({
      ...prev,
      items: prev.items.map(row => (row.id === rowId ? { ...row, [field]: value } : row)),
    }))
  }

  const selectedPileSize = shipping.selectedPileSize as PileCode | ''
  const setSelectedPileSize = (code: PileCode | '') => {
    setShipping(prev => ({ ...prev, selectedPileSize: code }))
  }

  const deliverySite = shipping.deliverySite
  const updateDeliverySite = (patch: Partial<DeliverySiteState>) => {
    setShipping(prev => ({ ...prev, deliverySite: { ...prev.deliverySite, ...patch } }))
  }

  const payment = shipping.payment as PaymentState
  const updatePayment = (patch: Partial<PaymentState>) => {
    setShipping(prev => ({ ...prev, payment: { ...prev.payment, ...patch } }))
  }

  const deliveryNotes = shipping.deliveryNotes
  const setDeliveryNotes = (value: string) => {
    setShipping(prev => ({ ...prev, deliveryNotes: value }))
  }

  const installationItems = installation.items
  const addInstallationItemRow = () => {
    setInstallation(prev => ({
      ...prev,
      items: [...prev.items, createEmptyInstallationLineItem()],
    }))
  }
  const removeInstallationItemRow = (rowId: string) => {
    setInstallation(prev => ({
      ...prev,
      items:
        prev.items.length <= 1 ? prev.items : prev.items.filter(row => row.id !== rowId),
    }))
  }
  const updateInstallationItemRow = (
    rowId: string,
    field: keyof Omit<InstallationLineItem, 'id'>,
    value: string | boolean
  ) => {
    setInstallation(prev => ({
      ...prev,
      items: prev.items.map(row => (row.id === rowId ? { ...row, [field]: value } : row)),
    }))
  }

  const installationPileSize = installation.selectedPileSize as InstallationPileCode | ''
  const setInstallationPileSize = (code: InstallationPileCode | '') => {
    setInstallation(prev => ({ ...prev, selectedPileSize: code }))
  }

  const pilingSite = installation.pilingSite
  const updatePilingSite = (patch: Partial<InstallationPilingSiteState>) => {
    setInstallation(prev => ({ ...prev, pilingSite: { ...prev.pilingSite, ...patch } }))
  }

  const craneSchedule = installation.craneSchedule
  const updateCraneSchedule = (patch: Partial<InstallationCraneScheduleState>) => {
    setInstallation(prev => ({ ...prev, craneSchedule: { ...prev.craneSchedule, ...patch } }))
  }

  const customerDuty = installation.customerDuty
  const updateCustomerDuty = (patch: Partial<InstallationCustomerDutyState>) => {
    setInstallation(prev => ({ ...prev, customerDuty: { ...prev.customerDuty, ...patch } }))
  }
  const toggleCustomerDuty = (key: InstallationCustomerDutyKey) => {
    setInstallation(prev => ({
      ...prev,
      customerDuty: { ...prev.customerDuty, [key]: !prev.customerDuty[key] },
    }))
  }

  const installationContract = installation.contract
  const updateInstallationContract = (patch: Partial<InstallationContractState>) => {
    setInstallation(prev => ({ ...prev, contract: { ...prev.contract, ...patch } }))
  }

  const installationPayment = installation.payment as InstallationFinanceState
  const updateInstallationPayment = (patch: Partial<InstallationFinanceState>) => {
    setInstallation(prev => ({ ...prev, payment: { ...prev.payment, ...patch } }))
  }
  const setInstallationFinanceStatus = (status: InstallationFinanceStatus) => {
    setInstallation(prev => ({ ...prev, payment: { ...prev.payment, status } }))
  }

  const signatures = installation.signatures
  const updateSignature = (
    block: keyof InstallationSignaturesState,
    patch: Partial<InstallationSignatureBlock>
  ) => {
    setInstallation(prev => ({
      ...prev,
      signatures: { ...prev.signatures, [block]: { ...prev.signatures[block], ...patch } },
    }))
  }

  const rigFooter = installation.rigFooter
  const updateRigFooter = (patch: Partial<InstallationRigFooterState>) => {
    setInstallation(prev => ({ ...prev, rigFooter: { ...prev.rigFooter, ...patch } }))
  }

  const handleNoticeTypeChange = (type: WorkOpeningNoticeType) => {
    setNoticeType(type)
  }

  const save = async () => {
    if (!id || isView) return
    setSaving(true)
    const surveyId = parseInt(id)

    if (noticeType === 'shipping') {
      const payload = shippingFormToApi(shipping)
      const { success, data } = await putWorkOpeningNoticeShipping(surveyId, payload)
      setSaving(false)
      if (!success) {
        toast.error(data)
        return
      }
      setShipping(apiToShippingForm(data, surveyPrefill))
    } else {
      const payload = installationFormToApi(installation)
      const { success, data } = await putWorkOpeningNoticeInstallation(surveyId, payload)
      setSaving(false)
      if (!success) {
        toast.error(data)
        return
      }
      setInstallation(apiToInstallationForm(data, surveyPrefill))
    }

    Swal.fire({
      title: 'สำเร็จ',
      text: 'บันทึกใบแจ้งเปิดงานสำเร็จ',
      icon: 'success',
      confirmButtonText: 'ตกลง',
    })
  }

  const onDownloadPdf = async () => {
    if (!id) return
    setDownloading(true)
    toast.info('กำลังดาวน์โหลดไฟล์ PDF กรุณารอสักครู่...')
    const { success, data } = await downloadWorkOpeningNoticePdf(parseInt(id), noticeType)
    setDownloading(false)
    if (!success) {
      toast.error(data)
      return
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `work_opening_notice_${noticeType}_${id}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return {
    isView,
    loading,
    saving,
    downloading,
    noticeType,
    setNoticeType: handleNoticeTypeChange,
    noticeTitle,
    headerDoc,
    updateHeaderDoc,
    sendTo,
    setSendTo,
    items,
    addItemRow,
    removeItemRow,
    updateItemRow,
    selectedPileSize,
    setSelectedPileSize,
    deliverySite,
    updateDeliverySite,
    payment,
    updatePayment,
    deliveryNotes,
    setDeliveryNotes,
    installationItems,
    addInstallationItemRow,
    removeInstallationItemRow,
    updateInstallationItemRow,
    installationPileSize,
    setInstallationPileSize,
    pilingSite,
    updatePilingSite,
    craneSchedule,
    updateCraneSchedule,
    customerDuty,
    updateCustomerDuty,
    toggleCustomerDuty,
    installationContract,
    updateInstallationContract,
    installationPayment,
    updateInstallationPayment,
    setInstallationFinanceStatus,
    signatures,
    updateSignature,
    rigFooter,
    updateRigFooter,
    save,
    onDownloadPdf,
  }
}

export default useViewModel
